import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Loader2, Ban, Trash2, Check, X } from "lucide-react";
import { Button } from "./ui/button";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  title: string;
  description: string;
  itemName?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "destructive" | "default";
  type?: "delete" | "disable" | "enable" | "default";
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  title,
  description,
  itemName,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  type = "default",
}: ConfirmDialogProps) {
  const getIcon = () => {
    switch (type) {
      case "delete":
        return Trash2;
      case "disable":
        return Ban;
      case "enable":
        return Check;
      default:
        return AlertTriangle;
    }
  };

  const getConfirmIcon = () => {
    switch (type) {
      case "delete":
        return <Trash2 className="w-4 h-4 mr-2" />;
      case "disable":
        return <Ban className="w-4 h-4 mr-2" />;
      case "enable":
        return <Check className="w-4 h-4 mr-2" />;
      default:
        return null;
    }
  };

  const getGradientColors = () => {
    switch (variant) {
      case "destructive":
        return "from-red-600 to-red-700";
      default:
        if (type === "enable") {
          return "from-green-600 to-emerald-600";
        }
        if (type === "disable") {
          return "from-orange-600 to-amber-600";
        }
        return "from-blue-600 to-indigo-600";
    }
  };

  const getButtonStyle = () => {
    switch (variant) {
      case "destructive":
        return "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/25";
      default:
        if (type === "enable") {
          return "bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/25";
        }
        if (type === "disable") {
          return "bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-600/25";
        }
        return "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/25";
    }
  };

  const getWarningBox = () => {
    if (type === "delete") {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-red-50 border border-red-200 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Warning: This action cannot be undone
              </p>
              <p className="text-sm text-red-700 mt-1">
                The item will be permanently deleted from the system.
              </p>
            </div>
          </div>
        </motion.div>
      );
    }

    if (type === "disable") {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-orange-50 border border-orange-200 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <Ban className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-orange-800">
                Product will be hidden from customers
              </p>
              <p className="text-sm text-orange-700 mt-1">
                This product will not be visible to customers and cannot be
                purchased.
              </p>
            </div>
          </div>
        </motion.div>
      );
    }

    if (type === "enable") {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">
                Product will be visible to customers
              </p>
              <p className="text-sm text-green-700 mt-1">
                This product will be available for purchase and visible to
                customers.
              </p>
            </div>
          </div>
        </motion.div>
      );
    }

    return null;
  };

  const IconComponent = getIcon();

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
          style={{
            margin: 0,
            padding: "1rem",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{
              type: "spring",
              duration: 0.5,
              bounce: 0.3,
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div
              className={`relative bg-gradient-to-r ${getGradientColors()} p-6 flex-shrink-0`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <IconComponent className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-xl font-bold text-white"
                    >
                      {title}
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      {type === "delete"
                        ? "Confirm deletion"
                        : type === "disable"
                        ? "Confirm disable"
                        : type === "enable"
                        ? "Confirm enable"
                        : "Confirm action"}
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    disabled={loading}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gradient-to-b from-gray-50/50 to-white">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-4"
              >
                <div className="text-center">
                  <p className="text-gray-700 text-base leading-relaxed">
                    {description}
                  </p>
                  {itemName && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <span className="text-sm font-medium text-gray-600">
                        Item:
                      </span>
                      <p className="text-base font-semibold text-gray-900 mt-1">
                        "{itemName}"
                      </p>
                    </div>
                  )}
                </div>

                {getWarningBox()}
              </motion.div>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-end gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={loading}
                    className="px-6 h-11 rounded-xl font-medium"
                  >
                    {cancelText}
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="button"
                    onClick={onConfirm}
                    disabled={loading}
                    className={`px-8 h-11 rounded-xl font-semibold ${getButtonStyle()}`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {type === "delete"
                          ? "Deleting..."
                          : type === "disable"
                          ? "Disabling..."
                          : type === "enable"
                          ? "Enabling..."
                          : "Processing..."}
                      </>
                    ) : (
                      <>
                        {getConfirmIcon()}
                        {confirmText}
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
